/**
 * Which config keys a form shows and which it requires. Mirrors the SDK's
 * `libs/sync-plugin-sdk/src/utils/config-visibility.ts`; one conformance fixture pins both.
 */

import { isPlainObject } from 'es-toolkit';

/** A well-formed `visibleWhen`. */
interface Condition {
  key: string;
  in: ReadonlySet<string>;
}

/** How one key's visibility is decided: always, never, or by a condition on another key. */
type Rule = boolean | Condition;

/**
 * The visible keys of `schema.properties`, in `Object.keys` order: those without `visibleWhen`, or
 * whose controller is visible and holds a listed value. `values` not a plain object read as none.
 */
export function visibleKeys(schema: unknown, values: unknown): string[] {
  const properties = ownEntries(isPlainObject(schema) ? schema.properties : undefined);
  const stored = ownEntries(values);

  const rules = new Map<string, Rule>();
  for (const [key, property] of properties) {
    rules.set(key, ruleOf(property, properties));
  }

  const resolved = new Map<string, boolean>();
  for (const key of properties.keys()) {
    resolveChain(key, rules, resolved, controller =>
      effectiveValue(properties.get(controller), stored.get(controller))
    );
  }
  return [...properties.keys()].filter(key => resolved.get(key) === true);
}

/** The schema's `required` narrowed to the visible keys, in its own order. */
export function effectiveRequired(schema: unknown, values: unknown): string[] {
  const required: unknown = isPlainObject(schema) ? schema.required : undefined;
  if (!Array.isArray(required)) {
    return [];
  }
  const visible = new Set(visibleKeys(schema, values));
  return required.filter((key): key is string => typeof key === 'string' && visible.has(key));
}

/**
 * Resolves `start` and every unresolved controller above it, walking up and then back down.
 * A loop rather than recursion, so a long chain cannot exhaust the stack.
 */
function resolveChain(
  start: string,
  rules: ReadonlyMap<string, Rule>,
  resolved: Map<string, boolean>,
  valueOf: (controller: string) => string | undefined
): void {
  const pending: Array<{ key: string; condition: Condition }> = [];
  const onPath = new Set<string>();
  let current = start;
  let controllerVisible: boolean;

  for (;;) {
    const known = resolved.get(current);
    if (known !== undefined) {
      controllerVisible = known;
      break;
    }
    // Back on the path: a cycle, which hides every key on it and every key leading to it.
    if (onPath.has(current)) {
      controllerVisible = false;
      break;
    }
    // A key without a rule is not declared; `ruleOf` never points a condition at one.
    const rule = rules.get(current) ?? false;
    if (typeof rule === 'boolean') {
      resolved.set(current, rule);
      controllerVisible = rule;
      break;
    }
    pending.push({ key: current, condition: rule });
    onPath.add(current);
    current = rule.key;
  }

  for (const { key, condition } of pending.reverse()) {
    const value = controllerVisible ? valueOf(condition.key) : undefined;
    controllerVisible = value !== undefined && condition.in.has(value);
    resolved.set(key, controllerVisible);
  }
}

/** The rule for one property; a malformed property or condition, or an unknown controller, hides it. */
function ruleOf(property: unknown, properties: ReadonlyMap<string, unknown>): Rule {
  if (!isPlainObject(property)) {
    return false;
  }
  const declared: unknown = property.visibleWhen;
  // `null` reads as absent, the way a manifest may spell "no condition".
  if (declared === undefined || declared === null) {
    return true;
  }
  if (!isPlainObject(declared)) {
    return false;
  }
  const controller: unknown = declared.key;
  const listed: unknown = declared.in;
  if (typeof controller !== 'string' || !properties.has(controller) || !Array.isArray(listed)) {
    return false;
  }
  return {
    key: controller,
    in: new Set(listed.filter((value): value is string => typeof value === 'string'))
  };
}

/** What a controller holds, as a condition compares it: the stored value, else the schema default. */
function effectiveValue(property: unknown, stored: unknown): string | undefined {
  const fallback: unknown = isPlainObject(property) ? property.default : undefined;
  return comparable(isEmpty(stored) ? fallback : stored);
}

/** Absent, `null` or a blank string — what the config form drops before it saves. */
function isEmpty(value: unknown): boolean {
  return (
    value === undefined || value === null || (typeof value === 'string' && value.trim() === '')
  );
}

/** A string as written, a boolean as `'true'` / `'false'`; anything else matches no condition. */
function comparable(value: unknown): string | undefined {
  if (typeof value === 'boolean') {
    return String(value);
  }
  return typeof value === 'string' && !isEmpty(value) ? value : undefined;
}

/** The own entries of a plain object, so a prototype member is never read as a key. */
function ownEntries(value: unknown): Map<string, unknown> {
  return new Map(isPlainObject(value) ? Object.entries(value) : []);
}
